import { Injectable } from '@angular/core';
import {
  ChatMessage,
  ReportAssistantInput,
  ReportAssistantStats,
  ReportSnapshot
} from 'src/app/models/report-assistant.model';
import { environment } from 'src/environments/environment';
import { REPORT_CATEGORIES } from './constant';

/**
 * Everything "AI" about the report page lives here (no UI code):
 *   ask()            -> entry point used by the panel component
 *   buildSnapshot()  -> flattens the report into one plain object
 *   answerWithBackend() -> optional LLM call (environment().assistant_url)
 *   answerLocally()  -> built-in analyzer, used when no backend is set or it fails
 */
@Injectable({ providedIn: 'root' })
export class ReportAssistantService {
  /**
   * Answers a question about the report. Never throws: tries the backend first (if configured),
   * then falls back to the built-in analyzer.
   */
  async ask(
    question: string,
    report: ReportAssistantInput,
    history: ChatMessage[] = []
  ): Promise<ChatMessage> {
    const snapshot = this.buildSnapshot(report);
    try {
      return await this.answerWithBackend(question, snapshot, history);
    } catch (err) {
      return this.answerLocally(question, snapshot);
    }
  }

  /** Small counters for the panel header chips */
  getStats(report: ReportAssistantInput): ReportAssistantStats {
    let partsInspected = 0;
    let defectsCount = 0;
    for (const c of REPORT_CATEGORIES) {
      const items = this.items(report.ratings, c.key);
      partsInspected += items.length;
      defectsCount += items.filter((i) => this.isDefect(i)).length;
    }
    return {
      partsInspected,
      defectsCount,
      flagsCount: Array.isArray(report.flags) ? report.flags.length : 0
    };
  }

  /** Everything on the page, flattened into one object the assistant can reason over */
  buildSnapshot(report: ReportAssistantInput): ReportSnapshot {
    const { vinInfo, ratings } = report;
    const parts = REPORT_CATEGORIES.map((c) => {
      const items = this.items(ratings, c.key);
      const defects = items
        .filter((i) => this.isDefect(i))
        .map((i) => ({
          part: i.parent_part,
          severity: +i.vpcs_severity,
          severityLabel: this.severityLabel(i.vpcs_severity),
          conditions: i.vpcs_conditions || [],
          labour: this.num(i.labour_cost),
          partsCost: this.num(i.part_cost),
          repair: this.num(i.repair_cost),
          description: i.description || ''
        }));
      return {
        category: c.label,
        inspected: items.length,
        good: items.length - defects.length,
        defects,
        repairTotal: defects.reduce((s, d) => s + d.repair, 0)
      };
    });

    return {
      title: vinInfo?.['vehicle_item_code'],
      calculatedValue: +report.calculatedScore || null,
      perceivedValue: +report.perceivedScore || null,
      marketValue: this.num(ratings?.['market_value']),
      purchaseValue: this.num(ratings?.['purchase_value']),
      totalRepairCost: this.num(ratings?.['total_repair_cost']),
      specs: Object.fromEntries(
        (report.specFields || []).map((s) => [s.label, this.specValue(vinInfo, s.key)])
      ),
      flags: Array.isArray(report.flags) ? report.flags : [],
      options: Array.isArray(report.options) ? report.options.map((o) => o['title']) : [],
      parts
    };
  }

  /**
   * Optional: if environment().assistant_url is set, the whole report snapshot + chat
   * history is POSTed there so a real LLM can answer. Expected response: { reply: string }.
   * If it is not configured (or fails), ask() falls back to answerLocally().
   */
  private async answerWithBackend(
    question: string,
    snapshot: ReportSnapshot,
    history: ChatMessage[]
  ): Promise<ChatMessage> {
    const url = (environment() as any)?.assistant_url;
    if (!url) throw new Error('no assistant endpoint');
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        report: snapshot,
        messages: history.map((m) => ({ role: m.role, content: m.text })),
        question
      })
    });
    if (!res.ok) throw new Error('assistant error');
    const data = await res.json();
    return { role: 'bot', text: data.reply, followUps: this.followUpsFor('') };
  }

  // ───────────────────────── helpers ─────────────────────────

  private items(ratings: any, key: string): any[] {
    const list = ratings?.[key];
    return Array.isArray(list) ? list : [];
  }

  /** A part is a defect when it is rated below "Good part • 5" */
  private isDefect(item: any): boolean {
    return !!item.vpcs_severity && +item.vpcs_severity < 5;
  }

  private num(v: any): number {
    return +String(v ?? '').replace(/[^0-9.]/g, '') || 0;
  }

  private specValue(vinInfo: any, key: string): string {
    const v = vinInfo?.[key];
    if (v && typeof v === 'object') return v['value'] ? String(v['value']) : 'N/A';
    return v ? String(v) : 'N/A';
  }

  private severityLabel(sev: any): string {
    return (
      {
        '1': 'Part change • 1',
        '2': 'Major Repair • 2',
        '3': 'Moderate Repair • 3',
        '4': 'Minor Repair • 4',
        '5': 'Good part • 5'
      }[String(sev)] || 'N/A'
    );
  }

  private grade(score: any): { cls: string; key: string } | null {
    const s = +score;
    if (!s) return null;
    if (s >= 5) return { cls: 'excellent', key: 'grading.excellent' };
    if (s >= 4.5) return { cls: 'good', key: 'grading.good' };
    if (s >= 4) return { cls: 'v-fair', key: 'grading.v_fair' };
    if (s >= 3.5) return { cls: 'fair', key: 'grading.fair' };
    if (s >= 3) return { cls: 'avg', key: 'grading.average' };
    if (s >= 2.5) return { cls: 'b-avg', key: 'grading.below_average' };
    if (s >= 2) return { cls: 'poor', key: 'grading.poor' };
    if (s >= 1.5) return { cls: 'v-poor', key: 'grading.v_poor' };
    return { cls: 'junk', key: 'grading.junk' };
  }

  private fmt(n: number) {
    return 'SAR ' + Math.round(n).toLocaleString('en-US');
  }

  private followUpsFor(topic: string): string[] {
    const all: Record<string, string[]> = {
      summary: ['Is the asking price fair?', 'What are the biggest defects?'],
      price: ['How much will repairs cost?', 'Should I bid on this car?'],
      defects: ['How much will repairs cost?', 'Should I bid on this car?'],
      repair: ['What are the biggest defects?', 'Should I bid on this car?'],
      parts: ['What are the biggest defects?', 'Does it have any flags?'],
      bid: ['Is the asking price fair?', 'What are the biggest defects?'],
      options: ['Give me a quick summary of this car', 'Does it have any flags?'],
      flags: ['What are the biggest defects?', 'Should I bid on this car?'],
      score: ['What are the biggest defects?', 'Is the asking price fair?']
    };
    return all[topic] || ['Give me a quick summary of this car', 'What are the biggest defects?'];
  }

  /** Built-in analyzer: answers purely from the data on this page */
  answerLocally(question: string, r: ReportSnapshot): ChatMessage {
    const q = question.toLowerCase();
    const grade = this.grade(r.calculatedValue);
    const allDefects = r.parts
      .flatMap((p) => p.defects.map((d) => ({ ...d, category: p.category })))
      .sort((a, b) => a.severity - b.severity || b.repair - a.repair);
    const repairSum = r.totalRepairCost || r.parts.reduce((s, p) => s + p.repairTotal, 0);
    const bot = (text: string, topic: string, bullets?: string[]): ChatMessage => ({
      role: 'bot',
      text,
      bullets,
      followUps: this.followUpsFor(topic)
    });
    const has = (...w: string[]) => w.some((x) => q.includes(x));

    if (has('bid', 'buy', 'recommend', 'worth', 'should i', 'purchase this')) {
      const good = (r.calculatedValue || 0) >= 4;
      const margin = r.marketValue && r.purchaseValue ? r.marketValue - r.purchaseValue : 0;
      const bullets = [
        `Calculated value ${r.calculatedValue ?? 'N/A'} / 5${grade ? ' (' + grade.cls.replace('-', ' ') + ')' : ''}`,
        allDefects.length
          ? `${allDefects.length} part(s) need attention, about ${this.fmt(repairSum)} to fix`
          : 'No defective parts were recorded',
        margin > 0
          ? `Dealer purchase value sits ${this.fmt(margin)} below market value`
          : 'No clear gap between market and purchase value'
      ];
      return bot(
        good
          ? 'On the numbers this looks like a reasonable car to bid on. Keep your top bid at or below the dealer purchase value so repairs stay inside your margin.'
          : 'This car needs a closer look before you bid. Price in the repairs and keep your top bid well under the purchase value.',
        'bid',
        bullets
      );
    }

    if (has('price', 'fair', 'market', 'value', 'cheap', 'expensive', 'worth')) {
      if (!r.marketValue && !r.purchaseValue) {
        return bot(
          'The report does not list a market or purchase value for this vehicle yet.',
          'price'
        );
      }
      const net = r.marketValue - repairSum;
      return bot(
        `Market value is ${r.marketValue ? this.fmt(r.marketValue) : 'not listed'} and the dealer purchase value is ${r.purchaseValue ? this.fmt(r.purchaseValue) : 'not listed'}.`,
        'price',
        [
          repairSum ? `Estimated repairs: ${this.fmt(repairSum)}` : 'No repair cost recorded',
          r.marketValue && repairSum ? `Market value after repairs: about ${this.fmt(net)}` : '',
          r.perceivedValue
            ? `Perceived value is ${r.perceivedValue} / 5`
            : 'Perceived value is N/A, so the calculated value is used as market value'
        ].filter(Boolean)
      );
    }

    if (has('repair', 'cost', 'fix', 'labour', 'labor', 'spend', 'budget')) {
      if (!allDefects.length && !repairSum) {
        return bot(
          'No repairs are recorded in this report, so there is no repair cost to budget for.',
          'repair'
        );
      }
      const top = [...allDefects].sort((a, b) => b.repair - a.repair).slice(0, 4);
      return bot(
        `Total estimated repair cost is ${repairSum ? this.fmt(repairSum) : 'not itemised'}. Workshop prices vary, so treat this as a guide.`,
        'repair',
        top.map((d) => `${d.part} (${d.category}): ${d.repair ? this.fmt(d.repair) : 'N/A'}`)
      );
    }

    if (has('defect', 'damage', 'problem', 'issue', 'wrong', 'worst', 'bad', 'scratch', 'dent')) {
      if (!allDefects.length) {
        return bot(
          'Good news: every inspected part is rated good. No defects are recorded.',
          'defects'
        );
      }
      return bot(
        `${allDefects.length} part(s) are below "Good". These are the most serious:`,
        'defects',
        allDefects
          .slice(0, 5)
          .map(
            (d) =>
              `${d.part} (${d.category}) - ${d.severityLabel}${d.conditions.length ? ', ' + d.conditions.join(', ') : ''}`
          )
      );
    }

    if (has('exterior', 'interior', 'mechanic', 'frame', 'condition', 'body', 'parts', 'how is')) {
      return bot(
        'Here is how each area came out:',
        'parts',
        r.parts
          .filter((p) => p.inspected)
          .map(
            (p) =>
              `${p.category}: ${p.good}/${p.inspected} parts good` +
              (p.defects.length
                ? `, ${p.defects.length} need work (${this.fmt(p.repairTotal)})`
                : '')
          )
      );
    }

    if (has('flag', 'warning', 'accident', 'flood', 'risk')) {
      return bot(
        r.flags.length
          ? `${r.flags.length} flag(s) are raised on this report:`
          : 'No flags are raised on this report.',
        'flags',
        r.flags.length ? r.flags.map((f) => f.charAt(0).toUpperCase() + f.slice(1)) : undefined
      );
    }

    if (has('option', 'feature', 'equipment', 'sunroof', 'extras')) {
      return bot(
        r.options.length
          ? 'The vehicle lists these options:'
          : 'No options are listed for this vehicle.',
        'options',
        r.options.length ? r.options.slice(0, 10) : undefined
      );
    }

    if (has('score', 'grade', 'rating', 'calculated', 'perceived', 'cv', 'pv')) {
      return bot(
        `The calculated value is ${r.calculatedValue ?? 'N/A'} out of 5. It compares good parts to defective parts and weighs how severe each defect is. Perceived value ${r.perceivedValue ? 'is ' + r.perceivedValue : 'is N/A'}; when it is missing the calculated value acts as the market value.`,
        'score'
      );
    }

    if (
      has(
        'mileage',
        'engine',
        'spec',
        'year',
        'color',
        'colour',
        'transmission',
        'drive',
        'model',
        'brand'
      )
    ) {
      const s = r.specs;
      return bot(`Key specs for ${r.title || 'this vehicle'}:`, 'summary', [
        `${s['Year']} ${s['Brand']} ${s['Model']} (${s['Body Type']})`,
        `Engine: ${s['Engine']}, ${s['Transmission']}, ${s['Drive Type']}`,
        `Mileage: ${s['Mileage']}`,
        `Color: ${s['Exterior Color']}`
      ]);
    }

    if (has('summary', 'summar', 'overview', 'about', 'tell me', 'quick', 'this car')) {
      return bot(
        `${r.title || 'This vehicle'} scores ${r.calculatedValue ?? 'N/A'} / 5${grade ? ' (' + grade.cls.replace('-', ' ') + ')' : ''}.`,
        'summary',
        [
          `Market value: ${r.marketValue ? this.fmt(r.marketValue) : 'N/A'}; purchase value: ${r.purchaseValue ? this.fmt(r.purchaseValue) : 'N/A'}`,
          allDefects.length
            ? `${allDefects.length} part(s) need attention (about ${this.fmt(repairSum)})`
            : 'All inspected parts are in good condition',
          r.flags.length ? `Flags: ${r.flags.join(', ')}` : 'No flags raised',
          `${r.specs['Mileage']} mileage, ${r.specs['Transmission']}, ${r.specs['Drive Type']}`
        ]
      );
    }

    return bot(
      'I can only answer from what is in this report. Try asking about the price, defects, repair costs, condition by area, flags or whether to bid.',
      'summary'
    );
  }
}
